import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { ZitaLogo, ZitaWordmark } from '@/components/ZitaLogo';
import { PublicFooter } from '@/components/PublicFooter';
import { toast } from 'sonner';
import { ShieldCheck, KeyRound, Lock, Mail } from 'lucide-react';

/**
 * Owner model v2.1 (Aug 2026) — Super Admin Step-Up.
 *
 * There is exactly ONE privileged identity: the Super Admin.
 * Authentication:
 *   1. Sign in on `/login` with email + password (normal customer login).
 *   2. Come here (`/admin/login`), enter the admin PIN.
 *   3. Backend mints a fresh JWT with `adm=true` for the same session.
 *
 * If the user hasn't signed in yet, this page collects both email + password
 * + PIN and runs the single-shot `POST /api/admin/login` endpoint that
 * combines the two flows. Either path lands the user in `/admin`.
 */
const AdminLogin = () => {
  const { user, loading, adminLogin, adminStepUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  // If already signed in — hide the email + password fields, only need PIN.
  const stepUpMode = !!user && !loading;

  useEffect(() => {
    if (user && user.email && !email) setEmail(user.email);
  }, [user, email]);

  const submit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      toast.error('Enter your admin PIN.');
      return;
    }
    setBusy(true);
    try {
      if (stepUpMode) {
        const u = await adminStepUp(pin.trim());
        if (!u?.is_admin_active) {
          toast.error('Admin PIN verification failed.');
          return;
        }
      } else {
        if (!email || !password) {
          toast.error('Enter your email and password.');
          return;
        }
        const u = await adminLogin(email.trim().toLowerCase(), password, pin.trim());
        if (!u?.is_admin_active) {
          toast.error('Admin PIN verification failed.');
          return;
        }
      }
      toast.success('Super Admin controls unlocked.');
      navigate('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <ZitaLogo size={44} />
          <ZitaWordmark className="text-3xl" />
        </Link>
        <form onSubmit={submit} className="zita-card p-8 w-full max-w-md space-y-4" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-zita-espresso text-zita-cream flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="font-serif text-2xl text-zita-espresso">Super Admin</div>
              <div className="text-xs text-zita-mocha">
                {stepUpMode
                  ? 'Enter your admin PIN to step up.'
                  : 'Email + password + admin PIN.'}
              </div>
            </div>
          </div>

          {!stepUpMode && (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-zita-muted">Email</span>
                <div className="relative mt-1">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zita-mocha" />
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-login-email"
                    className="zita-input w-full pl-9 pr-4 py-3 text-sm"
                  />
                </div>
              </label>

              <label className="block">
                <span className="text-xs uppercase tracking-wider text-zita-muted">Password</span>
                <div className="relative mt-1">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zita-mocha" />
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-login-password"
                    className="zita-input w-full pl-9 pr-4 py-3 text-sm"
                  />
                </div>
              </label>
            </>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-wider text-zita-muted">Admin PIN</span>
            <div className="relative mt-1">
              <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zita-mocha" />
              <input
                type="password"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="one-time-code"
                required
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                data-testid="admin-login-pin"
                className="zita-input w-full pl-9 pr-4 py-3 text-sm tracking-widest"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="zita-btn-primary w-full"
          >
            {busy ? 'Verifying…' : stepUpMode ? 'Unlock Super Admin' : 'Enter Super Admin'}
          </button>

          <div className="text-[11px] text-zita-muted text-center leading-relaxed">
            Failed attempts are rate-limited and logged. Session ends when you sign out.
          </div>
        </form>
      </main>
      <PublicFooter />
    </div>
  );
};

export default AdminLogin;
